import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import ButtonHelp from "@atoms/ButtonHelp";
import { ProductCard } from "@atoms/ProductCard";
import { Section } from "@atoms/Section";
import { WideCard } from "@atoms/WideCard";
import color from "@definitions/mui/color";
import Text from "@ds/components/Text";
import { formattedDate } from "@helpers/dateTime";
import useResponsive from "@helpers/useResponsive";
import Footer from "@layouts/Footer";
import useStyles from "../styles";
import TabPartial from "./partials/Tab";
import { ChevronRightBold } from "@icons/mobile";
import { useRouter } from "next/router";
import { ProductCardCarouselData } from "@atoms/ProductCard/ProductCardCarousel";

const InsightUltimateContainer: React.FC<any> = ({ dataNavbar, insightList }) => {
  const isTablet = useResponsive("lg");
  const isMobile = useResponsive("md");
  const classes = useStyles({ isVisible: true, isDesktop: true });
  const { t } = useTranslation("common");
  const router = useRouter();

  const tabletPadding = isTablet ? "110px" : "auto";

  const SectionTitle: React.FC<any> = ({ title, spacing, withBorderSeparator, section }) => {

    const label: string = section === "article" ? t("insight.article") : section === "story" ? t("insight.successStory") : t("insight.researchBusiness");
    const queryStr: string = section === "article" ? "Article" : section === "story" ? "Success Story" : "Business Research";

    return (
      <Box bgcolor="white">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt="20px"
          pb={spacing}
          sx={{
            margin: "0 auto",
            width: "100%",
            borderTop: withBorderSeparator ? `1px solid ${color.grey.light}` : 0,
          }}
        >
          <Text variant="h2" weight="700">
            {title}
          </Text>
          <Text
            variant="body1"
            weight="700"
            onClick={() => router.push(`/insight/list?category=${queryStr}`)}
            clickable
            sx={{
              color: color.primary.main,
              display: "inline-flex",
              alignItems: "center",
              svg: {
                margin: "0 16px 0 8px",
              },
            }}
          >
            {`${t("viewAll")} ${label}`}
            < ChevronRightBold />
          </Text>
        </Box>
      </Box>
    );
  };



  const renderSections = () => {
    if (!insightList?.data) {
      return;
    }
    const restData: ProductCardCarouselData[] = [...insightList.data];
    const parsedCategoriesDataForCarousel = (category: string) => {
      const temp = [...restData].filter(
        (item) => `${item?.categoryId}`.toLowerCase() === category.toLowerCase(),
      );

      return (
        temp
          .map((item: any) => ({
            id: item.id,
            image: item.imageUrl.mediaPath,
            linkTo: `/insight/${item.slug}`,
            subTitle: formattedDate(item.date),
            title: item.title,
            label: item.category,
            story: item.story
          }))
          .splice(0, 4) || []
      );
    };

    const risetBusinessList = parsedCategoriesDataForCarousel('business_research');
    const successStoriesList = parsedCategoriesDataForCarousel('success_story');
    const articleList = parsedCategoriesDataForCarousel('article');
    const windowInnerHeight = typeof window === "undefined" ? 0 : window.innerHeight;

    const firstSection = (
      <Section maxWidth="full-width" pb={0} pt={0} px={tabletPadding}>
        <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
          <Box id="artikel" className={classes.anchor}>
            <SectionTitle title={t("insight.article")} spacing="24px" section="article" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} width={isMobile ? "100%" : undefined}>
                {articleList?.[0] && (
                  <ProductCard
                    title={articleList[0].title}
                    htmlDescription={articleList[0].story}
                    textColor={color.general.main}
                    subTitle={formattedDate(articleList[0].subTitle)}
                    image={articleList[0].image + "?tr=w-640"}
                    label={articleList[0].label}
                    imageHeight={isMobile ? 200 - (windowInnerHeight < 800 ? 10 : 0) : 300}
                    imgQuality={50}
                    height={isMobile ? "auto" : 474}
                    overflow="hidden"
                    linkTo={`${articleList[0].linkTo}`}
                    withImagePlaceholder
                    priority
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {articleList?.slice(1).map((item: any) => (
                  <WideCard
                    key={item.id}
                    date={formattedDate(item.date)}
                    description={item.story}
                    title={item.title}
                    image={item.image + "?tr=w-400"}
                    linkTo={`${item.linkTo}`}
                    subtitle={item.label}
                    type="border"
                  />
                ))}
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Section>
    );

    return (
      <Box mt={"calc(70px + 153px)"}>
        {firstSection}
        <Box id="riset" className={classes.anchor}>
          <Section maxWidth="full-width" pt={0} pb={1} px={tabletPadding}>
            <Container maxWidth="lg" disableGutters>
              <SectionTitle title={t("insight.researchBusiness")} spacing="24px" section="research" />
              <Grid container spacing={3}>
                {risetBusinessList.map((item: any) => (
                  <Grid item xs={3} mb={2} key={item.id} width={isMobile ? "100%" : undefined}>
                    <ProductCard
                      title={item.title}
                      htmlDescription={item.story}
                      subTitle={item.subTitle}
                      image={item.image + "?tr=w-500"}
                      imageHeight={148}
                      imgQuality={50}
                      height="345px"
                      overflow="hidden"
                      withImagePlaceholder
                      titleMaxLine={3}
                      linkTo={`${item.linkTo}`}
                      label={item.label}
                      textColor={color.general.main}
                    />
                  </Grid>
                ))}
                {/* {loadMore()} */}
              </Grid>
            </Container>
          </Section>
        </Box>
        <Box id="cerita" className={classes.anchor}>
          <Section maxWidth="full-width" pt={0} pb={0} px={tabletPadding}>
            <Container maxWidth="lg" disableGutters>
              <SectionTitle title={t("insight.successStory")} spacing="24px" section="story" />
              <Grid container spacing={3}>
                {successStoriesList.map((item: any) => (
                  <Grid item xs={3} mb={2} key={item.id} width={isMobile ? "100%" : undefined}>
                    <ProductCard
                      title={item.title}
                      htmlDescription={item.story}
                      subTitle={item.subTitle}
                      image={item.image + "?tr=w-500"}
                      imageHeight={148}
                      imgQuality={50}
                      height="345px"
                      overflow="hidden"
                      withImagePlaceholder
                      titleMaxLine={3}
                      linkTo={`${item.linkTo}`}
                      label={item.label}
                      textColor={color.general.main}
                    />
                  </Grid>
                ))}
                {/* {loadMore()} */}
              </Grid>
            </Container>
          </Section>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        className={classes.base}
        id="insight"
        sx={{
          img: {
            maxHeight: "200px",
          },
        }}
      >
        <Box pb="44px">
          <Box className={classes.stickyHeader} pt="20px" px={tabletPadding}>
            <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
              <Box mb={1}>
                <Text
                  align={isMobile ? "center" : "left"}
                  variant="h1"
                  weight="700"
                  sx={{ fontSize: 32 }}
                >
                  {t("insight.title")}
                </Text>
              </Box>
              <Text
                variant="body1"
                weight="400"
                color="inactive-secondary"
                align={isMobile ? "center" : "left"}
                component="p"
              >
                {t("insight.desc")}
              </Text>
            </Container>
            <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
              <Box mt="36px">
                <TabPartial />
              </Box>
            </Container>
          </Box>
          {renderSections()}
        </Box>
        <ButtonHelp />
        <Footer dataNavbar={dataNavbar} />
      </Box>
    </>
  );
};

export default InsightUltimateContainer;
